/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { isProductionEnvironment } from 'src/core/utils/api/environment';
import { noop } from 'src/core/utils/base';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { detectLanguageDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    detectLanguageApiReferenceLink,
    detectLanguageDocLink,
    detectLanguageOverviewLink,
    detectLanguageSamplesLink,
    detectLanguageSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { detectLanguageDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { DemoId } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.detectLanguageDemoPage.pageHeader', defaultMessage: 'Detect language' },
    capabilityDescription: {
        id: 'languageStudio.detectLanguageDemoPage.capabilityDescription',
        defaultMessage: `The Language Detection feature of the Azure Text Analytics REST API
        evaluates text input for each document and returns language identifiers with a score
        that indicates the strength of the analysis. This capability is useful for content stores
        that collect arbitrary text, where language is unknown. You can parse the results of this
        analysis to determine which language is used in the input document. The response also
        returns a score that reflects the confidence of the model. The score value is between 0 and 1.`
    },
    emptyState: {
        id: 'languageStudio.detectLanguageDemoPage.emptyState',
        defaultMessage: `Your results will appear here once you enter or upload some text and press Run.
        The file format needs to be .txt`
    }
});

/**
 * Displays the Detect Language demo page.
 */
export const DetectLanguageDemoPage = observer(() => {
    const { localize } = useLocalization();

    const { tracker, detectLanguage, getCurlCommand, resetResults, detectedLanguages } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const handleDocumentChange = (newDocument: string) => {
        if (detectedLanguages) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Language Detection');
        }

        detectLanguage({ document, sampleTitle: selectedSampleTitle });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Language Detection"
                demoVersion={LuisConstants.Demos.ApiVersions.GA}
                getSdkLink={detectLanguageSdkLink}
                supportedPlatforms={['docker', 'cloud']}
                demoTitle={localize(messages.pageHeader)}
                documentationLink={detectLanguageDocLink}
                learnMoreLink={detectLanguageOverviewLink}
                gitHubSamplesLink={detectLanguageSamplesLink}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                selectedLanguage={null}
                onLanguageChange={noop}
                disableLanguageSelection
                featureName="Language Detection"
                acceptedResourceType="TextAnalytics"
                samplesList={detectLanguageDemoSamples}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                supportedRegionLink={detectLanguageApiReferenceLink}
                languagesList={detectLanguageDemoSupportedLanguages}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}>
                <DemoPageAgreement
                    featureName="Language Detection"
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<DetectedLanguagesDocuments>
                results={detectedLanguages}
                isLoading={tracker.isRunning}
                featureName="Language Detection"
                emptyStateMessage={localize(messages.emptyState)}>
                <LanguageCard language={detectedLanguages?.documents[0].detectedLanguage} />

                <Stack tokens={{ childrenGap: 10, padding: '10px 0 0 0' }}>
                    <LuisText as="h3" fontWeight={500}>{localize(commonDemosMessages.originalText)}</LuisText>
                    <LuisText>{document}</LuisText>
                </Stack>
            </ResultsStep>

            <NextStepsStep
                sdkLink={detectLanguageSdkLink}
                featureName="Language Detection"
                isResultsEmpty={!detectedLanguages}
                documentationLink={detectLanguageDocLink}
                sampleCodeLink={detectLanguageSamplesLink}
                curlCommand={getCurlCommand({ endpoint: 'LanguageDetection', text: document })}
                demosToDisplay={[
                    'sentiment',
                    'ner',
                    'pii',
                    'keyPhrases',
                    ...(!isProductionEnvironment() ? ['summarization' as DemoId] : [])
                ]}
            />
        </Stack>
    );
});
